import { Box, Stack, Typography } from '@mui/material';
import { DefinitionGrid, FormGrid, FormGridItem, FormSection, FormTextField } from '../../../design-system';
import {
  businessRegistrationHelper,
  characterLength,
  corporateRegistrationHelper,
  formatBusinessRegistrationNumber,
  formatCorporateRegistrationNumber,
  todayInSeoul,
  validBusinessRegistrationNumber,
  validCorporateRegistrationNumber,
  validFoundedOn,
  validMainPhoneNumber,
} from '../model/companyLegal';
import type { CompanyLegalProfile } from '../model/types';

export type CompanyLegalEditableField =
  | 'descriptor'
  | 'legalName'
  | 'mainPhoneNumber'
  | 'foundedOn'
  | 'businessRegistrationNumber'
  | 'corporateRegistrationNumber'
  | 'representativeTitle'
  | 'representativeName'
  | 'registeredAddress';

export function CompanyLegalFields({
  profile,
  disabled,
  readOnly,
  onChange,
}: {
  profile: CompanyLegalProfile;
  disabled: boolean;
  readOnly: boolean;
  onChange: (field: CompanyLegalEditableField, value: string) => void;
}) {
  return (
    <Stack spacing={3}>
      <FormSection heading="회사 식별">
        <FormGrid>
          <FormGridItem wide testId="company-legal-field-descriptor">
            <FormTextField
              required
              label="업무상 표시명"
              value={profile.descriptor}
              disabled={disabled}
              error={characterLength(profile.descriptor) < 1 || characterLength(profile.descriptor) > 200}
              help="목록, 검색과 회사 선택에 사용하는 이름입니다."
              onChange={(event) => onChange('descriptor', event.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 200,
                  readOnly,
                },
              }}
            />
          </FormGridItem>
          <FormGridItem wide testId="company-legal-field-legal-name">
            <FormTextField
              required
              label="법인명"
              value={profile.legalName}
              disabled={disabled}
              error={characterLength(profile.legalName) < 1 || characterLength(profile.legalName) > 200}
              help="사업자등록증과 등기사항증명서에 기재된 공식 명칭입니다."
              onChange={(event) => onChange('legalName', event.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 200,
                  readOnly,
                },
              }}
            />
          </FormGridItem>
          <Box data-testid="company-legal-field-main-phone-number">
            <FormTextField
              label="회사 대표 전화번호"
              value={profile.mainPhoneNumber}
              disabled={disabled}
              error={!validMainPhoneNumber(profile.mainPhoneNumber)}
              helperText={
                validMainPhoneNumber(profile.mainPhoneNumber)
                  ? '선택 입력 · 예: 02-1234-5678'
                  : '숫자를 하나 이상 포함하고 + ( ) . - 공백만 사용해 주세요.'
              }
              onChange={(event) => onChange('mainPhoneNumber', event.target.value)}
              slotProps={{
                htmlInput: {
                  inputMode: 'tel',
                  maxLength: 50,
                  readOnly,
                },
              }}
            />
          </Box>
          <Box data-testid="company-legal-field-founded-on">
            <FormTextField
              type="date"
              label="설립일"
              value={profile.foundedOn}
              disabled={disabled}
              error={!validFoundedOn(profile.foundedOn)}
              helperText={
                validFoundedOn(profile.foundedOn)
                  ? undefined
                  : '오늘 이후의 날짜는 입력할 수 없습니다.'
              }
              onChange={(event) => onChange('foundedOn', event.target.value)}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  min: '0001-01-01',
                  max: todayInSeoul(),
                  readOnly,
                },
              }}
            />
          </Box>
      </FormGrid>
      </FormSection>
      <FormSection heading="법인 등록">
        <FormGrid>
          <Box data-testid="company-legal-field-business-registration-number">
            <FormTextField
              label="사업자등록번호"
              value={profile.businessRegistrationNumber}
              disabled={disabled}
              error={!validBusinessRegistrationNumber(profile.businessRegistrationNumber)}
              helperText={businessRegistrationHelper(profile.businessRegistrationNumber)}
              onChange={(event) =>
                onChange('businessRegistrationNumber', formatBusinessRegistrationNumber(event.target.value))
              }
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  maxLength: 12,
                  readOnly,
                },
              }}
            />
          </Box>
          <Box data-testid="company-legal-field-corporate-registration-number">
            <FormTextField
              label="법인등록번호"
              value={profile.corporateRegistrationNumber}
              disabled={disabled}
              error={!validCorporateRegistrationNumber(profile.corporateRegistrationNumber)}
              helperText={corporateRegistrationHelper(profile.corporateRegistrationNumber)}
              onChange={(event) =>
                onChange('corporateRegistrationNumber', formatCorporateRegistrationNumber(event.target.value))
              }
              slotProps={{
                htmlInput: {
                  inputMode: 'numeric',
                  maxLength: 14,
                  readOnly,
                },
              }}
            />
          </Box>
      </FormGrid>
      </FormSection>
      <FormSection heading="증명서 발급 정보">
        <FormGrid>
          <Box data-testid="company-legal-field-representative-title">
            <FormTextField
              required
              label="대표자 직함"
              value={profile.representativeTitle}
              disabled={disabled}
              error={
                characterLength(profile.representativeTitle) < 1 || characterLength(profile.representativeTitle) > 80
              }
              help="증명서 서명 영역에 표시되는 직함입니다."
              onChange={(event) => onChange('representativeTitle', event.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 80,
                  readOnly,
                },
              }}
            />
          </Box>
          <Box data-testid="company-legal-field-representative-name">
            <FormTextField
              required
              label="대표자 이름"
              value={profile.representativeName}
              disabled={disabled}
              error={characterLength(profile.representativeName) < 1 || characterLength(profile.representativeName) > 100}
              help="증명서 서명 영역에 표시되는 이름입니다."
              onChange={(event) => onChange('representativeName', event.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 100,
                  readOnly,
                },
              }}
            />
          </Box>
          <FormGridItem wide testId="company-legal-field-registered-address">
            <FormTextField
              required
              fullWidth
              multiline
              minRows={2}
              label="본점 주소"
              value={profile.registeredAddress}
              disabled={disabled}
              error={characterLength(profile.registeredAddress) < 1 || characterLength(profile.registeredAddress) > 500}
              help="증명서 발급 주체 주소로 자동 적용됩니다."
              onChange={(event) => onChange('registeredAddress', event.target.value)}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                  readOnly,
                },
              }}
            />
          </FormGridItem>
        </FormGrid>
      </FormSection>
    </Stack>
  );
}
export function CompanyLegalSummary({ profile }: { profile: CompanyLegalProfile }) {
  const items = [
    {
      id: 'descriptor',
      label: '업무상 표시명',
      value: profile.descriptor,
      wide: true,
    },
    {
      id: 'legal-name',
      label: '법인명',
      value: profile.legalName,
      wide: true,
    },
    {
      id: 'main-phone-number',
      label: '회사 대표 전화번호',
      value: profile.mainPhoneNumber,
      wide: false,
    },
    {
      id: 'founded-on',
      label: '설립일',
      value: profile.foundedOn,
      wide: false,
    },
    {
      id: 'business-registration-number',
      label: '사업자등록번호',
      value: profile.businessRegistrationNumber,
      wide: false,
    },
    {
      id: 'corporate-registration-number',
      label: '법인등록번호',
      value: profile.corporateRegistrationNumber,
      wide: false,
    },
    {
      id: 'representative-title',
      label: '대표자 직함',
      value: profile.representativeTitle,
      wide: false,
    },
    {
      id: 'representative-name',
      label: '대표자 이름',
      value: profile.representativeName,
      wide: false,
    },
    {
      id: 'registered-address',
      label: '본점 주소',
      value: profile.registeredAddress,
      wide: true,
    },
  ] as const;
  const groups = [
    { title: '회사 식별', items: items.slice(0, 4) },
    { title: '법인 등록', items: items.slice(4, 6) },
    { title: '증명서 발급 정보', items: items.slice(6) },
  ];
  return <Stack spacing={3} data-testid="company-legal-summary">
    {groups.map((group) => <Stack key={group.title} spacing={1.5}>
      <Typography component="h3" variant="subtitle2">{group.title}</Typography>
      <DefinitionGrid items={group.items.map((item) => ({
        ...item,
        value: <Box component="span" data-testid={`company-legal-summary-${item.id}`}>{item.value || '미입력'}</Box>,
      }))} />
    </Stack>)}
  </Stack>;
}
